// Deterministic parser for Slovenian due-date phrases in a spoken reminder, e.g.
//   "jutri ob desetih pokliči Marka" -> tomorrow 10:00
//   "v petek do dvanajstih pošlji ponudbo" -> next Friday 12:00
//   "čez dve uri preveri beton" -> now + 2 h
//   "15. novembra oddaj PZI" -> 15 Nov (all-day, 9:00)
// Times are interpreted in `timeZone` (Europe/Ljubljana); the result is an ISO string in UTC.
//
// The input comes from speech recognition, so the parser works on a folded copy of the text
// (lower case, diacritics removed: "čez" == "cez") and accepts numbers written as digits or words.
// Only phrases with an explicit date/time cue ("ob", "do", "čez", weekday, month name, ...) are
// recognised, so quantities, prices and phone numbers ("kupi 5 vreč", "041 123 456") never match;
// neither do counted things after a cue ("do treh variant", "ob treh oknih") or drawing scales
// ("detajl 1:20").
//
// Resolution rules (documented here because they are product decisions, not grammar):
//  * A clock hour 1–6 without "zjutraj"/"dopoldne"/"ponoči" is read as afternoon (+12 h):
//    on a building site nobody schedules a 3 a.m. reminder, but "ob treh" for 15:00 is common.
//    Hours written with a leading zero ("05:30") are taken literally.
//  * "popoldne"/"zvečer" move hours before noon to the afternoon/evening ("ob osmih zvečer" = 20:00).
//  * A time without a day means today if it is at least one minute ahead, otherwise tomorrow.
//  * A day without a time means 09:00 local and `allDay: true`. When that day is today and 09:00
//    (or the part-of-day default, e.g. "nocoj" = 19:00) has already passed, the time is the next
//    full quarter hour instead, so a new reminder is never overdue the moment it is created.
//  * A weekday means its next occurrence. Today's weekday counts only when a time later than now is
//    given ("v soboto ob 18h" on a Saturday morning); otherwise it is the same weekday next week.
//    With "ta"/"to" ("ta petek") today's weekday means today.
//  * Day/month without a year that already passed this year means next year.
//  * "naslednji teden" = next Monday 09:00, "konec tedna" = Friday 15:00, "konec meseca" = last
//    day of the month 09:00.
//  * When the text contains several date expressions, the most specific one wins (date + time beats
//    date alone); a day and a time mentioned separately are combined when that is unambiguous.

export interface DueParseResult {
  /** ISO 8601 UTC instant. */
  dueAt: string;
  /** The exact substring of the input that was recognised. */
  matchedText: string;
  /** True when only a day was recognised (time defaulted to 09:00 local, or the next quarter hour today). */
  allDay: boolean;
}

const MINUTE_MS = 60_000;
const QUARTER_HOUR_MS = 15 * MINUTE_MS;
const DAY_MS = 86_400_000;
const DEFAULT_DAY_TIME = 9 * 60;
const END_OF_WEEK_TIME = 15 * 60;

// ---------------------------------------------------------------------------------------------
// Time zone helpers (Intl only, DST-correct)
// ---------------------------------------------------------------------------------------------

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClock(ms: number, timeZone: string): WallClock {
  const out: WallClock = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of formatter(timeZone).formatToParts(new Date(ms))) {
    if (part.type === 'year' || part.type === 'month' || part.type === 'day') out[part.type] = Number(part.value);
    else if (part.type === 'hour' || part.type === 'minute' || part.type === 'second') out[part.type] = Number(part.value);
  }
  // Some ICU versions still print midnight as "24" despite hourCycle h23.
  if (out.hour === 24) out.hour = 0;
  return out;
}

/** UTC offset (local - UTC) in milliseconds that `timeZone` has at the instant `ms`. */
function offsetAt(ms: number, timeZone: string): number {
  const w = wallClock(ms, timeZone);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(ms / 1000) * 1000;
}

/**
 * Converts a wall-clock time in `timeZone` to a UTC instant. The offset is found by iteration
 * (guess the offset at the naive instant, re-evaluate it at the corrected instant until stable),
 * which is exact on either side of a DST change. Wall times inside the spring-forward gap
 * (02:30 on 2026-03-29 in Ljubljana) are moved forward by the gap; ambiguous wall times in the
 * autumn overlap resolve to the earlier instant, like Temporal's "compatible" disambiguation.
 */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone = 'Europe/Ljubljana',
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  let offset = offsetAt(naive, timeZone);
  let utc = naive - offset;
  for (let i = 0; i < 3; i++) {
    const next = offsetAt(utc, timeZone);
    if (next === offset) break;
    offset = next;
    utc = naive - offset;
  }
  const offsetBefore = offsetAt(naive - DAY_MS, timeZone);
  if (offsetAt(utc, timeZone) !== naive - utc) {
    // No instant has this wall time (DST gap): use the pre-transition offset, which moves forward.
    return new Date(naive - offsetBefore);
  }
  if (offsetBefore !== offset) {
    const earlier = naive - offsetBefore;
    if (earlier < utc && offsetAt(earlier, timeZone) === offsetBefore) utc = earlier;
  }
  return new Date(utc);
}

// ---------------------------------------------------------------------------------------------
// Calendar arithmetic on local dates (time-zone independent)
// ---------------------------------------------------------------------------------------------

interface LocalDate {
  y: number;
  m: number;
  d: number;
}

function addDays(date: LocalDate, days: number): LocalDate {
  const t = new Date(Date.UTC(date.y, date.m - 1, date.d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function addMonths(date: LocalDate, months: number): LocalDate {
  const index = date.y * 12 + (date.m - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return { y, m, d: Math.min(date.d, daysInMonth(y, m)) };
}

function compareDates(a: LocalDate, b: LocalDate): number {
  return a.y - b.y || a.m - b.m || a.d - b.d;
}

/** 0 = Monday ... 6 = Sunday. */
function isoWeekdayIndex(date: LocalDate): number {
  return (new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay() + 6) % 7;
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

// ---------------------------------------------------------------------------------------------
// Vocabulary (all forms are diacritics-free and lower case, matching the folded text)
// ---------------------------------------------------------------------------------------------

const UNIT_WORDS = ['', 'ena', 'dva', 'tri', 'stiri', 'pet', 'sest', 'sedem', 'osem', 'devet'];
const TEEN_WORDS = [
  'deset',
  'enajst',
  'dvanajst',
  'trinajst',
  'stirinajst',
  'petnajst',
  'sestnajst',
  'sedemnajst',
  'osemnajst',
  'devetnajst',
];
const TEN_WORDS = ['', '', 'dvajset', 'trideset', 'stirideset', 'petdeset', 'sestdeset'];
const ORDINAL_STEMS = ['', 'prv', 'drug', 'tretj', 'cetrt', 'pet', 'sest', 'sedm', 'osm', 'devet'];
const LOCATIVE_SPECIAL: Record<number, string[]> = {
  1: ['eni', 'enih'],
  2: ['dveh'],
  3: ['treh'],
  4: ['stirih'],
  7: ['sedmih'],
  8: ['osmih'],
};

/** Nominative cardinal 1..69, e.g. 25 -> "petindvajset". */
function cardinal(n: number): string {
  if (n < 10) return UNIT_WORDS[n] ?? '';
  if (n < 20) return TEEN_WORDS[n - 10] ?? '';
  const tens = Math.floor(n / 10);
  const unit = n % 10;
  return unit === 0 ? (TEN_WORDS[tens] ?? '') : `${UNIT_WORDS[unit]}in${TEN_WORDS[tens]}`;
}

/** Locative/genitive plural used after "ob"/"do"/"v": "desetih", "dveh", "eni". */
function locatives(n: number): string[] {
  return LOCATIVE_SPECIAL[n] ?? [`${cardinal(n)}ih`];
}

function ordinalStem(n: number): string {
  return n < 10 ? (ORDINAL_STEMS[n] ?? '') : cardinal(n);
}

/** Count words for "čez N dni", "v N dneh": nominative and locative forms 1..60. */
const COUNT_WORDS = new Map<string, number>([
  ['en', 1],
  ['eno', 1],
  ['enega', 1],
  ['enem', 1],
  ['enim', 1],
  ['dve', 2],
]);
for (let n = 1; n <= 60; n++) {
  COUNT_WORDS.set(cardinal(n), n);
  for (const w of locatives(n)) COUNT_WORDS.set(w, n);
}

/** Hour words that are unambiguously a time after "do"/"okoli" (locative / genitive forms). */
const HOUR_LOCATIVE = new Map<string, number>([['ene', 1]]);
/** Colloquial nominative/accusative hours ("ob tri", "četrt na eno"), accepted only after "ob"/"na". */
const HOUR_NOMINATIVE = new Map<string, number>([
  ['eno', 1],
  ['dve', 2],
]);
const HOUR_ORDINAL = new Map<string, number>();
for (let n = 1; n <= 24; n++) {
  for (const w of locatives(n)) HOUR_LOCATIVE.set(w, n);
  HOUR_NOMINATIVE.set(cardinal(n), n);
  HOUR_ORDINAL.set(`${ordinalStem(n)}i`, n);
}

const MINUTE_WORDS = new Map<string, number>();
for (let n = 5; n <= 55; n += 5) MINUTE_WORDS.set(cardinal(n), n);

const DAY_ORDINALS = new Map<string, number>();
for (let n = 1; n <= 31; n++) {
  DAY_ORDINALS.set(`${ordinalStem(n)}ega`, n);
  DAY_ORDINALS.set(`${ordinalStem(n)}i`, n);
}

const MONTH_FORMS = new Map<string, number>();
(
  [
    ['januar', 'januarja'],
    ['februar', 'februarja'],
    ['marec', 'marca'],
    ['april', 'aprila'],
    ['maj', 'maja'],
    ['junij', 'junija'],
    ['julij', 'julija'],
    ['avgust', 'avgusta'],
    ['september', 'septembra'],
    ['oktober', 'oktobra'],
    ['november', 'novembra'],
    ['december', 'decembra'],
  ] as const
).forEach((forms, i) => {
  for (const f of forms) MONTH_FORMS.set(f, i + 1);
});

/** Weekday forms -> JS weekday (0 = Sunday). */
const WEEKDAY_FORMS = new Map<string, number>([
  ['ponedeljek', 1],
  ['ponedeljka', 1],
  ['torek', 2],
  ['torka', 2],
  ['sreda', 3],
  ['sredo', 3],
  ['srede', 3],
  ['cetrtek', 4],
  ['cetrtka', 4],
  ['petek', 5],
  ['petka', 5],
  ['sobota', 6],
  ['soboto', 6],
  ['sobote', 6],
  ['nedelja', 0],
  ['nedeljo', 0],
  ['nedelje', 0],
]);

type PartOfDay = 'morning' | 'forenoon' | 'afternoon' | 'evening' | 'night';

const PART_FORMS = new Map<string, PartOfDay>([
  ['zjutraj', 'morning'],
  ['zjutri', 'morning'],
  ['dopoldne', 'forenoon'],
  ['dopoldan', 'forenoon'],
  ['popoldne', 'afternoon'],
  ['popoldan', 'afternoon'],
  ['zvecer', 'evening'],
  ['ponoci', 'night'],
]);

const PART_DEFAULT_TIME: Record<PartOfDay, number> = {
  morning: 8 * 60,
  forenoon: 10 * 60,
  afternoon: 15 * 60,
  evening: 19 * 60,
  night: 22 * 60,
};

// ---------------------------------------------------------------------------------------------
// Regex building blocks
// ---------------------------------------------------------------------------------------------

const B = '(?<![\\p{L}\\p{N}])';
const E = '(?![\\p{L}\\p{N}])';

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Alternation, longest first so that "petnajst" is preferred over "pet". */
function alt(words: Iterable<string>): string {
  return [...new Set(words)]
    .sort((a, b) => b.length - a.length)
    .map(escapeRe)
    .join('|');
}

const NUM = `\\d{1,3}(?:[.,]\\d{1,2})?|${alt(COUNT_WORDS.keys())}`;
const HOUR_ANY = alt([...HOUR_LOCATIVE.keys(), ...HOUR_NOMINATIVE.keys()]);
const HOUR_LOC = alt(HOUR_LOCATIVE.keys());
const HOUR_NOM = alt(HOUR_NOMINATIVE.keys());
const MIN_WORDS = alt(MINUTE_WORDS.keys());
const ORD_TIME = alt(HOUR_ORDINAL.keys());
const DAY_ORD = alt(DAY_ORDINALS.keys());
const MONTHS = alt(MONTH_FORMS.keys());
const WEEKDAYS = alt(WEEKDAY_FORMS.keys());
const PARTS = alt(PART_FORMS.keys());
/** "ob" variants accept bare numbers ("ob 10"); the others need an explicit time marker ("do 10h"). */
const TIME_PREFIX = alt([
  'ob',
  'okoli ob',
  'okrog ob',
  'priblizno ob',
  'cca ob',
  'okoli',
  'okrog',
  'priblizno',
  'cca',
  'do',
  'najkasneje do',
  'najpozneje do',
  'pred',
]).replace(/ /g, '\\s+');
const WEEK_MOD = 'naslednji|prihodnji|drugi|drug|ta';

/**
 * A number directly followed by one of these is a quantity, not a time ("ob 20 stopinjah",
 * "do dveh dni", "do 15 €").
 */
const QUANTITY_FOLLOWS = new RegExp(
  '^\\s*(?:[%°€$]|(?:' +
    [
      'stopinj\\p{L}*',
      'odstot\\p{L}*',
      'procent\\p{L}*',
      'evr(?:a|e|i|o|ov|om)?',
      'eur(?:a|o|ov)?',
      'kos(?:a|e|i|ov|ih)?',
      'vrec\\p{L}*',
      'kubik\\p{L}*',
      'metr\\p{L}*',
      'centimetr\\p{L}*',
      'milimetr\\p{L}*',
      'kilometr\\p{L}*',
      'kilogram\\p{L}*',
      'litr\\p{L}*',
      'ljudi',
      'oseb\\p{L}*',
      'delavc\\p{L}*',
      'palet\\p{L}*',
      'ton(?:a|e|i|o)?',
      'dni',
      'dneva',
      'dnevi',
      'tedn(?:a|e|ov)',
      'mesec(?:a|e|ev)',
      'let(?:a|i)?',
      'ur(?:a|e|i)?',
      'minut(?:a|e|i)?',
      'sekund\\p{L}*',
      'nadstrop\\p{L}*',
      'etaz\\p{L}*',
      'kom',
      'cm',
      'mm',
      'm',
      'm2',
      'm3',
      'km',
      'kg',
      'g',
      't',
      'l',
      'kw',
    ].join('|') +
    `)${E})`,
  'u',
);

/**
 * After an hour ("ob treh", "do petih", "ob 3") these mean the number counts things: a plural
 * genitive/locative adjective ("do treh novih ponudb") or a counted noun in the genitive/locative
 * plural ("do treh variant", "ob treh oknih", "okoli petih tisoč evrov"). Not used after dates:
 * "do 15. 11. ponudbe" is a deadline.
 */
const COUNTED_FOLLOWS = new RegExp(
  '^\\s*(?:\\p{L}{3,}ih|' +
    [
      'tisoc',
      'milijon(?:ov|ih)',
      'variant(?:ah)?',
      'ponudb(?:ah)?',
      'ponudnik(?:ov|ih)',
      'oken',
      'vrat',
      'kartus(?:ah)?',
      'izvod(?:ov|ih)',
      'predlog(?:ov|ih)',
      'nacrt(?:ov|ih)',
      'list(?:ov|ih)',
      'risb(?:ah)?',
      'kopij(?:ah)?',
      'strani|straneh',
      'tock(?:ah)?',
      'opcij(?:ah)?',
      'moznosti',
      'stanovanj',
      'prostor(?:ov|ih)',
      'sob(?:ah)?',
      'vzorc(?:ev|ih)',
      'komad(?:ov|ih)',
      'detajl(?:ov|ih)',
      'tloris(?:ov|ih)',
      'prerez(?:ov|ih)',
      'sten(?:ah)?',
      'plosc(?:ah)?',
      'mest',
      'enot(?:ah)?',
      'objekt(?:ov|ih)',
      'projekt(?:ov|ih)',
      'izvajalc(?:ev|ih)',
      'podizvajalc(?:ev|ih)',
      'clan(?:ov|ih)',
    ].join('|') +
    `)${E}`,
  'u',
);

function quantityAfterHour(after: string): boolean {
  return QUANTITY_FOLLOWS.test(after) || COUNTED_FOLLOWS.test(after);
}

// ---------------------------------------------------------------------------------------------
// Atoms: recognised pieces of a date/time expression
// ---------------------------------------------------------------------------------------------

type DayRef =
  | { type: 'offset'; days: number }
  | { type: 'months'; months: number }
  | { type: 'weekday'; weekday: number; mode: 'next' | 'this' | 'nextWeek' }
  | { type: 'date'; day: number; month: number; year: number | null }
  | { type: 'nextWeek' }
  | { type: 'endOfWeek'; weeksAhead: number }
  | { type: 'endOfMonth'; monthsAhead: number }
  | { type: 'nextMonth' };

interface Clock {
  hour: number;
  minute: number;
  /** Hour 1–12 written without a leading zero: subject to the afternoon / part-of-day rules. */
  adjustable: boolean;
}

interface AtomData {
  day?: DayRef;
  clock?: Clock;
  part?: PartOfDay;
  /** Relative instant: minutes from now ("čez dve uri"). */
  instant?: number;
}

interface Atom extends AtomData {
  start: number;
  end: number;
  priority: number;
}

interface Rule {
  re: RegExp;
  priority: number;
  handle: (groups: Record<string, string | undefined>, after: string) => AtomData | null;
}

function parseCount(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  if (/^\d/.test(raw)) {
    const n = Number(raw.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  return COUNT_WORDS.get(raw) ?? null;
}

function hourWord(raw: string): number | null {
  if (/^\d+$/.test(raw)) return Number(raw);
  return HOUR_LOCATIVE.get(raw) ?? HOUR_NOMINATIVE.get(raw) ?? null;
}

/** Clock for "pol X", "četrt na X", "N minut do X": minutes before the full hour X. */
function beforeHour(hourOf: number, minutesBefore: number): Clock | null {
  if (hourOf < 1 || hourOf > 24 || minutesBefore < 1 || minutesBefore > 59) return null;
  let hour = hourOf - 1;
  // "pol ene" is 12:30, not 0:30.
  if (hour === 0) hour = 12;
  return { hour, minute: 60 - minutesBefore, adjustable: hour >= 1 && hour <= 12 };
}

function digitClock(hourText: string, hour: number, minute: number): Clock | null {
  if (hour > 24 || minute > 59 || (hour === 24 && minute !== 0)) return null;
  const leadingZero = hourText.length === 2 && hourText.startsWith('0');
  return { hour, minute, adjustable: hour >= 1 && hour <= 12 && !leadingZero };
}

const rules: Rule[] = [];

function rule(source: string, priority: number, handle: Rule['handle']): void {
  rules.push({ re: new RegExp(source, 'gu'), priority, handle });
}

// "čez dve uri", "čez pol ure", "čez uro in pol", "v 2 urah", "čez 1,5 ure"
rule(
  `${B}(?<pre>cez|v)\\s+(?:(?<num>${NUM})\\s+)?(?:(?<frac>tri\\s*cetrt|cetrt|pol)\\s+)?(?<unit>ur(?:ah|[aeio])?|urc[eo])` +
    `(?:\\s+in\\s+(?:(?<half>pol)|(?<mins>${NUM})\\s+(?:minut(?:ah|[aeio])?|min)))?${E}`,
  3,
  (g) => {
    let minutes: number;
    if (g.frac) {
      // "tri četrt ure": the count pattern takes "tri" before the fraction pattern sees it.
      const threeQuarters = g.frac === 'cetrt' && parseCount(g.num) === 3;
      if (g.num && !threeQuarters) return null;
      minutes = g.frac === 'pol' ? 30 : threeQuarters || g.frac.startsWith('tri') ? 45 : 15;
    } else if (g.num) {
      const n = parseCount(g.num);
      if (n === null) return null;
      minutes = Math.round(n * 60);
    } else if (g.pre === 'cez' && (g.unit === 'uro' || g.unit === 'urco')) {
      minutes = 60;
    } else {
      return null;
    }
    if (g.half) minutes += 30;
    if (g.mins) {
      const extra = parseCount(g.mins);
      if (extra === null || extra >= 60 || !Number.isInteger(extra)) return null;
      minutes += extra;
    }
    return minutes > 0 && minutes <= 7 * 24 * 60 ? { instant: minutes } : null;
  },
);

// "čez 20 minut", "čez minuto", "v petih minutah"
rule(`${B}(?<pre>cez|v)\\s+(?:(?<num>${NUM})\\s+)?(?<unit>minut(?:ah|[aeio])?|min)${E}`, 3, (g) => {
  const n = g.num ? parseCount(g.num) : g.pre === 'cez' && g.unit === 'minuto' ? 1 : null;
  if (n === null) return null;
  const minutes = Math.round(n);
  return minutes > 0 && minutes <= 7 * 24 * 60 ? { instant: minutes } : null;
});

// "čez 3 dni", "čez teden", "čez dva tedna", "čez mesec", "v petih dneh"
rule(
  `${B}(?<pre>cez|v)\\s+(?:(?<num>${NUM})\\s+)?(?<unit>dan|dni|dneva|dnevi|dnevih|dneh|dnevu|teden|tedn(?:ov|ih|[aeu])|mesec(?:ev|ih|[aeu])?)${E}`,
  3,
  (g) => {
    const unit = g.unit ?? '';
    // "čez dan" means "during the day", so days always need a count; "v tednu" is not a deadline.
    const n = g.num ? parseCount(g.num) : g.pre === 'cez' && (unit === 'teden' || unit === 'mesec') ? 1 : null;
    if (n === null || !Number.isInteger(n) || n < 1) return null;
    if (unit.startsWith('d')) return n <= 366 ? { day: { type: 'offset', days: n } } : null;
    if (unit.startsWith('t')) return n <= 52 ? { day: { type: 'offset', days: n * 7 } } : null;
    return n <= 24 ? { day: { type: 'months', months: n } } : null;
  },
);

// "danes", "jutri", "pojutrišnjem"
rule(`${B}(?:do\\s+)?(?<w>danes|jutri|pojutrisnjem|pojutrisnem|pojutrisnjim)${E}`, 3, (g) => ({
  day: { type: 'offset', days: g.w === 'danes' ? 0 : g.w === 'jutri' ? 1 : 2 },
}));

// "nocoj", "drevi": today, evening
rule(`${B}(?:nocoj|drevi)${E}`, 3, () => ({ day: { type: 'offset', days: 0 }, part: 'evening' }));

// "zjutraj", "dopoldne", "popoldne", "zvečer", "ponoči"
rule(`${B}(?<w>${PARTS})${E}`, 3, (g) => {
  const part = PART_FORMS.get(g.w ?? '');
  return part ? { part } : null;
});

// "opoldne", "ob polnoči"
rule(
  `${B}(?:(?:ob|do|okoli|okrog|pred)\\s+)?(?:(?<noon>opoldne|opoldan|poldne)|(?<midnight>opolnoci|polnoci|polnoc))${E}`,
  4,
  (g) => ({ clock: { hour: g.noon ? 12 : 24, minute: 0, adjustable: false } }),
);

function weekdayFrom(g: Record<string, string | undefined>): number | null {
  return WEEKDAY_FORMS.get(g.wd ?? '') ?? null;
}

// "naslednji teden v petek", "v petek naslednji teden", "ta teden v četrtek"
rule(
  `${B}(?:(?:v|do|za)\\s+)?(?<mod>${WEEK_MOD})\\s+teden\\s+(?:(?:v|do|za)\\s+)?(?<wd>${WEEKDAYS})${E}`,
  4,
  (g) => {
    const weekday = weekdayFrom(g);
    if (weekday === null) return null;
    return { day: { type: 'weekday', weekday, mode: g.mod === 'ta' ? 'this' : 'nextWeek' } };
  },
);
rule(`${B}(?:(?:v|do|za)\\s+)?(?<wd>${WEEKDAYS})\\s+(?<mod>${WEEK_MOD})\\s+teden${E}`, 4, (g) => {
  const weekday = weekdayFrom(g);
  if (weekday === null) return null;
  return { day: { type: 'weekday', weekday, mode: g.mod === 'ta' ? 'this' : 'nextWeek' } };
});

// "v petek", "do petka", "ta petek", "naslednji ponedeljek", "prihodnjo sredo"
rule(
  `${B}(?:(?:v|do|za|najkasneje\\s+do|najpozneje\\s+do)\\s+)?` +
    `(?:(?<mod>ta|to|tale|tole|naslednji|naslednjo|naslednje|prihodnji|prihodnjo|prihodnje)\\s+)?(?<wd>${WEEKDAYS})${E}`,
  4,
  (g) => {
    const weekday = weekdayFrom(g);
    if (weekday === null) return null;
    const mode = g.mod && /^t/.test(g.mod) ? 'this' : 'next';
    return { day: { type: 'weekday', weekday, mode } };
  },
);

// "naslednji teden", "v prihodnjem tednu"
rule(`${B}(?:(?:naslednji|prihodnji|drugi|drug)\\s+teden|v\\s+(?:naslednjem|prihodnjem|drugem)\\s+tednu)${E}`, 3, () => ({
  day: { type: 'nextWeek' },
}));

// "konec tedna", "do konca meseca", "konec naslednjega tedna"
rule(
  `${B}(?:(?:do|ob|na)\\s+)?kon(?:ec|ca|cu|cem)\\s+(?:(?<which>tega|naslednjega|prihodnjega)\\s+)?(?<unit>tedna|meseca)${E}`,
  3,
  (g) => {
    const ahead = g.which === 'naslednjega' || g.which === 'prihodnjega' ? 1 : 0;
    return g.unit === 'tedna'
      ? { day: { type: 'endOfWeek', weeksAhead: ahead } }
      : { day: { type: 'endOfMonth', monthsAhead: ahead } };
  },
);

// "naslednji mesec", "v prihodnjem mesecu"
rule(`${B}(?:(?:naslednji|prihodnji)\\s+mesec|v\\s+(?:naslednjem|prihodnjem)\\s+mesecu)${E}`, 3, () => ({
  day: { type: 'nextMonth' },
}));

function dateAtom(day: number, month: number, year: number | null): AtomData | null {
  if (month < 1 || month > 12 || day < 1) return null;
  const max = year === null ? (month === 2 ? 29 : daysInMonth(2001, month)) : daysInMonth(year, month);
  if (day > max) return null;
  return { day: { type: 'date', day, month, year } };
}

// "15. 11.", "15.11.2026", "do 1. 12. 2026"
rule(
  `${B}(?:do\\s+)?(?<d>\\d{1,2})\\.\\s?(?<m>\\d{1,2})\\.(?:\\s?(?<y>(?:19|20)\\d{2})(?!\\d)\\.?)?(?!\\d)`,
  6,
  (g, after) => {
    if (QUANTITY_FOLLOWS.test(after)) return null;
    return dateAtom(Number(g.d), Number(g.m), g.y ? Number(g.y) : null);
  },
);

// "15. novembra", "15 novembra", "petnajstega novembra", "1. decembra 2026"
rule(
  `${B}(?:do\\s+)?(?:(?<d>\\d{1,2})\\.?|(?<dw>${DAY_ORD}))\\s*(?<mon>${MONTHS})(?:\\s+(?<y>(?:19|20)\\d{2})(?!\\d)\\.?)?${E}`,
  6,
  (g) => {
    const day = g.d ? Number(g.d) : (DAY_ORDINALS.get(g.dw ?? '') ?? 0);
    const month = MONTH_FORMS.get(g.mon ?? '') ?? 0;
    return dateAtom(day, month, g.y ? Number(g.y) : null);
  },
);

function isLenientPrefix(pre: string | undefined): boolean {
  return pre !== undefined && /(?:^|\s)ob$/.test(pre);
}

// "ob 10", "ob 10h", "ob 10.30", "ob 10:30", "ob 14ih", "do 12h", "ob 10. uri", "ob 10 in pol"
rule(
  `${B}(?<pre>${TIME_PREFIX})\\s+(?<h>\\d{1,2})(?:(?<sep>[.:,])(?<mi>\\d{2}))?(?!\\d|[.,:]\\d)` +
    `(?:\\s*(?<suf>-?ih|h|\\.?\\s*ur[aeio]))?(?:\\s+in\\s+(?<half>pol))?${E}`,
  5,
  (g, after) => {
    const hasMarker = Boolean(g.sep || g.suf || g.half);
    if (!hasMarker && (!isLenientPrefix(g.pre) || quantityAfterHour(after))) return null;
    if (g.half && g.mi) return null;
    const minute = g.half ? 30 : g.mi ? Number(g.mi) : 0;
    const clock = digitClock(g.h ?? '', Number(g.h), minute);
    return clock ? { clock } : null;
  },
);

// "ob desetih", "ob pol enajstih", "ob četrt na enajst", "ob tri četrt na enajst", "do dvanajstih",
// "ob deseti uri", "ob deset trideset", colloquial "ob tri"
rule(
  `${B}(?:(?<pre>${TIME_PREFIX})\\s+)?(?:(?<pol>pol)\\s+(?<polh>${HOUR_ANY}|\\d{1,2})` +
    `|(?<q>tri\\s*cetrt|cetrt)\\s+na\\s+(?<qh>${HOUR_NOM}|\\d{1,2})` +
    `|(?<hw>${HOUR_ANY})(?:\\s+(?:in\\s+)?(?:(?<half>pol)|(?<mw>${MIN_WORDS})))?` +
    `|(?<ord>${ORD_TIME})\\s+uri)${E}`,
  5,
  (g, after) => {
    const pre = g.pre;
    if (g.q) {
      const clock = beforeHour(hourWord(g.qh ?? '') ?? 0, g.q.startsWith('tri') ? 15 : 45);
      return clock ? { clock } : null;
    }
    if (quantityAfterHour(after)) return null;
    if (g.pol) {
      const raw = g.polh ?? '';
      // Without a prefix only the unmistakable genitive forms count ("pol enajstih", not "pol ene vreče").
      if (!pre && !(HOUR_LOCATIVE.has(raw) && raw.endsWith('h'))) return null;
      const clock = beforeHour(hourWord(raw) ?? 0, 30);
      return clock ? { clock } : null;
    }
    if (!pre) return null;
    if (g.ord) {
      const hour = HOUR_ORDINAL.get(g.ord) ?? 0;
      return hour ? { clock: { hour, minute: 0, adjustable: hour <= 12 } } : null;
    }
    const word = g.hw ?? '';
    // "do pet" is "up to five"; after "do"/"okoli" only the locative "do petih" is a time.
    if (!isLenientPrefix(pre) && !HOUR_LOCATIVE.has(word)) return null;
    const hour = hourWord(word);
    if (hour === null) return null;
    const minute = g.half ? 30 : g.mw ? (MINUTE_WORDS.get(g.mw) ?? 0) : 0;
    return { clock: { hour, minute, adjustable: hour >= 1 && hour <= 12 } };
  },
);

// "deset minut do osmih"
rule(
  `${B}(?:(?<pre>${TIME_PREFIX})\\s+)?(?<n>\\d{1,2}|${MIN_WORDS})\\s+minut\\s+do\\s+(?<h>${HOUR_ANY}|\\d{1,2})${E}`,
  5,
  (g) => {
    const n = /^\d/.test(g.n ?? '') ? Number(g.n) : (MINUTE_WORDS.get(g.n ?? '') ?? 0);
    const clock = beforeHour(hourWord(g.h ?? '') ?? 0, n);
    return clock && n > 0 ? { clock } : null;
  },
);

// Bare "10:30" (not a scale such as "merilo 1:50"). A bare "1:MM" is always a drawing scale for an
// architect ("detajl 1:20", "tloris M 1:50"); as a time it needs a prefix ("ob 1:30").
rule(
  `(?<![\\p{L}\\p{N}.,:])(?<!(?:merilo|merilu|merila|merilom|razmerje|razmerju|razmerja|razmerjem)\\s*)(?<h>\\d{1,2}):(?<mi>\\d{2})(?![\\d:])`,
  1,
  (g) => {
    if (g.h === '1') return null;
    const clock = digitClock(g.h ?? '', Number(g.h), Number(g.mi));
    return clock ? { clock } : null;
  },
);

// ---------------------------------------------------------------------------------------------
// Text folding with an index map back to the original string
// ---------------------------------------------------------------------------------------------

interface Folded {
  text: string;
  /** For each folded UTF-16 unit: [start, end) in the original string. */
  starts: number[];
  ends: number[];
}

function fold(input: string): Folded {
  let text = '';
  const starts: number[] = [];
  const ends: number[] = [];
  let i = 0;
  while (i < input.length) {
    const cp = input.codePointAt(i) ?? 0;
    const ch = String.fromCodePoint(cp);
    const next = i + ch.length;
    let folded = ch
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '');
    if (folded === 'đ') folded = 'd';
    if (folded.length === 0) {
      // A standalone combining mark: attach it to the previous character so substrings stay whole.
      if (ends.length > 0) ends[ends.length - 1] = next;
    } else {
      for (let k = 0; k < folded.length; k++) {
        starts.push(i);
        ends.push(next);
      }
      text += folded;
    }
    i = next;
  }
  return { text, starts, ends };
}

// ---------------------------------------------------------------------------------------------
// Matching, grouping, resolution
// ---------------------------------------------------------------------------------------------

function findAtoms(text: string): Atom[] {
  const candidates: Atom[] = [];
  for (const r of rules) {
    r.re.lastIndex = 0;
    for (const m of text.matchAll(r.re)) {
      const start = m.index;
      const end = start + m[0].length;
      const data = r.handle(m.groups ?? {}, text.slice(end));
      if (data) candidates.push({ ...data, start, end, priority: r.priority });
    }
  }
  // Longest match wins; on equal length the more specific rule (higher priority) wins.
  candidates.sort((a, b) => b.end - b.start - (a.end - a.start) || b.priority - a.priority || a.start - b.start);
  const accepted: Atom[] = [];
  for (const c of candidates) {
    if (accepted.every((a) => c.end <= a.start || c.start >= a.end)) accepted.push(c);
  }
  return accepted.sort((a, b) => a.start - b.start);
}

interface Group {
  atoms: Atom[];
  day?: DayRef;
  clock?: Clock;
  part?: PartOfDay;
  instant?: number;
}

function canJoin(group: Group, atom: Atom): boolean {
  if (group.instant !== undefined || atom.instant !== undefined) return false;
  return !(
    (atom.day && group.day) ||
    (atom.clock && group.clock) ||
    (atom.part && group.part)
  );
}

function join(group: Group, atom: Atom): void {
  group.atoms.push(atom);
  if (atom.day) group.day = atom.day;
  if (atom.clock) group.clock = atom.clock;
  if (atom.part) group.part = atom.part;
  if (atom.instant !== undefined) group.instant = atom.instant;
}

function newGroup(atom: Atom): Group {
  const g: Group = { atoms: [] };
  join(g, atom);
  return g;
}

/** Atoms belong to one expression when only a couple of words and no sentence break separate them. */
function adjacent(text: string, from: number, to: number): boolean {
  const gap = text.slice(from, to);
  if (/[.!?;:]/.test(gap)) return false;
  return gap.split(/[\s,]+/).filter(Boolean).length <= 2;
}

function groupAtoms(text: string, atoms: Atom[]): Group[] {
  const count = (pred: (a: Atom) => boolean) => atoms.filter(pred).length;
  const unambiguous =
    count((a) => a.instant !== undefined) === 0 &&
    count((a) => a.day !== undefined) <= 1 &&
    count((a) => a.clock !== undefined) <= 1 &&
    count((a) => a.part !== undefined) <= 1;
  if (unambiguous) {
    // At most one day, one time and one part of day in the whole text: they describe the same
    // moment even when other words sit between them ("jutri pokliči Marka ob desetih").
    const g: Group = { atoms: [] };
    for (const a of atoms) join(g, a);
    return [g];
  }
  const groups: Group[] = [];
  let current: Group | null = null;
  for (const atom of atoms) {
    const last = current?.atoms[current.atoms.length - 1];
    if (current && last && canJoin(current, atom) && adjacent(text, last.end, atom.start)) {
      join(current, atom);
    } else {
      current = newGroup(atom);
      groups.push(current);
    }
  }
  return groups;
}

function score(g: Group): number {
  if (g.instant !== undefined) return 5;
  let s = 0;
  if (g.day) s += g.day.type === 'date' ? 3 : 2;
  if (g.clock) s += 3;
  if (g.part) s += 1;
  return s;
}

/** Minutes since local midnight for a clock, applying the part-of-day and afternoon rules. */
function clockMinutes(clock: Clock, part: PartOfDay | undefined): number {
  let h = clock.hour;
  if (clock.adjustable) {
    switch (part) {
      case 'morning':
      case 'forenoon':
        break;
      case 'afternoon':
      case 'evening':
        if (h < 12) h += 12;
        break;
      case 'night':
        if (h === 12) h = 24;
        else if (h >= 7) h += 12;
        break;
      default:
        // Rule: 1–6 without a part of day is afternoon ("ob treh" = 15:00).
        if (h >= 1 && h <= 6) h += 12;
    }
  }
  return h * 60 + clock.minute;
}

interface Context {
  nowMs: number;
  today: LocalDate;
  timeZone: string;
}

function toUtcMs(date: LocalDate, minutes: number, timeZone: string): number {
  const d = addDays(date, Math.floor(minutes / 1440));
  const m = minutes % 1440;
  return zonedTimeToUtc(d.y, d.m, d.d, Math.floor(m / 60), m % 60, timeZone).getTime();
}

function resolveDay(day: DayRef, time: number, ctx: Context): LocalDate {
  const { today } = ctx;
  const idx = isoWeekdayIndex(today);
  switch (day.type) {
    case 'offset':
      return addDays(today, day.days);
    case 'months':
      return addMonths(today, day.months);
    case 'nextWeek':
      return addDays(today, 7 - idx);
    case 'nextMonth':
      return { ...addMonths({ ...today, d: 1 }, 1), d: 1 };
    case 'endOfMonth': {
      const month = addMonths({ ...today, d: 1 }, day.monthsAhead);
      return { ...month, d: daysInMonth(month.y, month.m) };
    }
    case 'endOfWeek': {
      if (day.weeksAhead > 0) return addDays(today, 7 - idx + 4 + 7 * (day.weeksAhead - 1));
      if (idx > 4) return addDays(today, 4 - idx + 7);
      const friday = addDays(today, 4 - idx);
      return idx === 4 && toUtcMs(friday, time, ctx.timeZone) <= ctx.nowMs ? addDays(friday, 7) : friday;
    }
    case 'weekday': {
      const target = (day.weekday + 6) % 7;
      if (day.mode === 'nextWeek') return addDays(today, 7 - idx + target);
      const diff = (target - idx + 7) % 7;
      if (diff > 0 || day.mode === 'this') return addDays(today, diff);
      return toUtcMs(today, time, ctx.timeZone) > ctx.nowMs ? today : addDays(today, 7);
    }
    case 'date': {
      let year = day.year ?? today.y;
      if (day.year === null && compareDates({ y: year, m: day.month, d: day.day }, today) < 0) year += 1;
      // 29 February without a year: the next leap year.
      while (day.year === null && day.day > daysInMonth(year, day.month) && !isLeapYear(year)) year += 1;
      return { y: year, m: day.month, d: Math.min(day.day, daysInMonth(year, day.month)) };
    }
  }
}

function resolve(g: Group, ctx: Context): { ms: number; allDay: boolean } {
  if (g.instant !== undefined) {
    return { ms: Math.floor(ctx.nowMs / MINUTE_MS) * MINUTE_MS + g.instant * MINUTE_MS, allDay: false };
  }
  const explicit = g.clock ? clockMinutes(g.clock, g.part) : g.part ? PART_DEFAULT_TIME[g.part] : null;
  if (!g.day) {
    // Time alone (explicit is always set here): today if still ahead, otherwise tomorrow.
    const time = explicit ?? DEFAULT_DAY_TIME;
    const todayMs = toUtcMs(ctx.today, time, ctx.timeZone);
    if (todayMs >= ctx.nowMs + MINUTE_MS) return { ms: todayMs, allDay: false };
    return { ms: toUtcMs(addDays(ctx.today, 1), time, ctx.timeZone), allDay: false };
  }
  const implied = g.day.type === 'endOfWeek' ? END_OF_WEEK_TIME : null;
  const time = explicit ?? implied ?? DEFAULT_DAY_TIME;
  const date = resolveDay(g.day, time, ctx);
  let ms = toUtcMs(date, time, ctx.timeZone);
  if (!g.clock && compareDates(date, ctx.today) === 0 && ms < ctx.nowMs + MINUTE_MS) {
    // Today with a default time that has already passed ("danes" at 15:00, "nocoj" at 20:30):
    // the next full quarter hour, so the reminder is not overdue the moment it is created.
    ms = Math.ceil((ctx.nowMs + MINUTE_MS) / QUARTER_HOUR_MS) * QUARTER_HOUR_MS;
  }
  return { ms, allDay: explicit === null && implied === null };
}

export function parseDueDate(text: string, now: Date, timeZone = 'Europe/Ljubljana'): DueParseResult | null {
  const nowMs = now.getTime();
  if (typeof text !== 'string' || text.trim() === '' || !Number.isFinite(nowMs)) return null;

  const folded = fold(text);
  const atoms = findAtoms(folded.text);
  if (atoms.length === 0) return null;

  const groups = groupAtoms(folded.text, atoms);
  let best: Group | undefined;
  for (const g of groups) {
    // Strictly greater: on a tie the earliest expression wins.
    if (!best || score(g) > score(best)) best = g;
  }
  if (!best) return null;

  const wall = wallClock(nowMs, timeZone);
  const ctx: Context = { nowMs, today: { y: wall.year, m: wall.month, d: wall.day }, timeZone };
  const { ms, allDay } = resolve(best, ctx);

  const first = best.atoms[0];
  const last = best.atoms[best.atoms.length - 1];
  if (!first || !last) return null;
  const start = folded.starts[first.start] ?? 0;
  const end = folded.ends[last.end - 1] ?? text.length;
  return { dueAt: new Date(ms).toISOString(), matchedText: text.slice(start, end), allDay };
}
